import { useDebounce } from "react-use";
import React, { useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Space, Drawer, Select, Spin, Row, Col } from "antd";

import { openNotificationWithIcon } from "utils";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { setIsInvalidateMultipleBatchDrawerOpen } from "pages/Generation/generationSlice";
import {
  useLazyGetBatchesToInvalidateQuery,
  useInvalidateBatchesFinalStatusMutation,
} from "pages/Generation/generationApiSlice";

const InvalidateMultipleBatchDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedBatches, setSelectedBatches] = useState<any>([]);
  const [searchValue, setSearchValue] = useState<any>({ key: "", value: "" });
  const { isInvalidateMultipleBatchDrawerOpen } = useAppSelector((state) => state.generation);

  const [getBatchesToInvalidate, getBatchesToInvalidateResult] =
    useLazyGetBatchesToInvalidateQuery();
  const [invalidateMultipleBatches, invalidateMultipleBatchesResult] =
    useInvalidateBatchesFinalStatusMutation();

  const closeDrawer = () => {
    dispatch(setIsInvalidateMultipleBatchDrawerOpen(false));
  };
  const [isReady, cancel] = useDebounce(
    () => getBatchesToInvalidate({ searchParam: searchValue.value }),
    800,
    [searchValue.key],
  );

  const handleInvalidateMultipleBatches = () => {
    invalidateMultipleBatches(selectedBatches)
      .unwrap()
      .then((payload) => {
        openNotificationWithIcon("successfully updated", payload.response, "success");
        closeDrawer();
        setSelectedBatches([]);
      })
      .catch((error) => {
        if (error.originalStatus >= 500 && error.originalStatus < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          openNotificationWithIcon(error?.data?.errorMessage, "", "warning");
        }
      })
      .finally(() => {});
  };

  return (
    <Drawer
      width={500}
      closable={false}
      placement="right"
      maskClosable={false}
      destroyOnClose={true}
      onClose={() => {
        closeDrawer();
        setSelectedBatches([]);
      }}
      title="Invalidate multiple batches"
      open={isInvalidateMultipleBatchDrawerOpen}
      extra={
        <Space>
          <Button
            type="text"
            onClick={() => {
              closeDrawer();
              setSelectedBatches([]);
            }}
          >
            <CloseOutlined />
          </Button>
        </Space>
      }
    >
      <Row>
        <Col span={24} style={{ marginBottom: 15 }}>
          <Select
            allowClear
            labelInValue
            mode="multiple"
            filterOption={false}
            style={{ width: "100%" }}
            placeholder="Select batches"
            onClear={() => setSelectedBatches([])}
            disabled={invalidateMultipleBatchesResult.isLoading}
            onSearch={(value) => {
              setSearchValue({ key: value, value: value });
            }}
            onSelect={({ value }) =>
              setSelectedBatches((prevBatches: string[]) => [...prevBatches, value])
            }
            onDeselect={({ value }) =>
              setSelectedBatches((prevBatches: string[]) =>
                prevBatches.filter((batch) => batch !== value),
              )
            }
            notFoundContent={
              getBatchesToInvalidateResult.isFetching && isReady() ? <Spin size="small" /> : null
            }
            options={
              getBatchesToInvalidateResult?.currentData?.response
                ? getBatchesToInvalidateResult?.currentData?.response?.map((value) => ({
                    key: value,
                    value: value,
                  }))
                : []
            }
          />
        </Col>
        <Col span={24}>
          <Button
            type="primary"
            loading={invalidateMultipleBatchesResult.isLoading}
            onClick={() => {
              cancel();
              handleInvalidateMultipleBatches();
            }}
          >
            Invalidate
          </Button>
        </Col>
      </Row>
    </Drawer>
  );
};

export default InvalidateMultipleBatchDrawer;
